import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtBRL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PublicNav from "@/components/Layout/PublicNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Cpu, Zap, Shield, HardDrive, Globe2, Terminal, ArrowRight, Check, Gauge, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";

const HERO_BG =
  "https://images.unsplash.com/photo-1714548529197-537c1f0b6aa7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHRlY2huaWNhbCUyMHdpcmVmcmFtZSUyMGRhcmt8ZW58MHx8fHwxNzgzMDUzMjk0fDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Zap, title: "SSD NVMe", desc: "I/O até 6x mais rápido que HDs comuns. Boot em segundos." },
  { icon: Shield, title: "Anti-DDoS", desc: "Mitigação em L3/L4 e L7 inclusa sem custo extra." },
  { icon: Globe2, title: "Datacenters TIER III", desc: "Múltiplas regiões, com uplink 10Gbps redundante." },
  { icon: Gauge, title: "Uptime 99.99%", desc: "SLA garantido. Créditos automáticos em caso de queda." },
  { icon: Cpu, title: "vCPU dedicada", desc: "Núcleos garantidos, sem overselling em nenhum plano." },
  { icon: Terminal, title: "Root & KVM", desc: "Acesso root imediato + console KVM via painel." },
];

const FAQS = [
  { q: "Como funciona o provisionamento?", a: "Planos Paris 🇫🇷: entrega pelo provider atual. Planos Brasil 🇧🇷: a criação automática começa somente após a confirmação do PIX; as credenciais aparecem no painel e por e-mail quando o acesso estiver disponível." },
  { q: "Posso trocar de plano depois?", a: "Sim. Upgrades são feitos com poucos cliques e cobrados pro-rata." },
  { q: "Vocês têm backup automático?", a: "Backups semanais em todos os planos e diários no Business para cima." },
  { q: "Aceitam qual forma de pagamento?", a: "PIX (Mercado Pago), cartão de crédito e boleto para pessoa jurídica." },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [locationTab, setLocationTab] = useState("PAR");
  const { addItem } = useCart();
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="hero-section">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/60 via-[#09090B]/80 to-[#09090B]" />
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1 rounded-sm mb-6" data-testid="hero-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 dot-blink" />
              <span className="text-xs font-mono tracking-widest uppercase text-zinc-400">
                Infraestrutura online — 47ms
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              VPS de alto desempenho <br />
              <span className="text-zinc-500">para quem constrói</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-xl">
              VPS sem penduricalhos. NVMe, anti-DDoS, root em 60s.
              Pague com PIX, gerencie tudo por um painel próprio no melhor estilo WHMCS.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp px-8" data-testid="hero-cta-plans">
                <a href="#planos">Ver planos <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-sm border-white/20 hover:bg-white hover:text-black" data-testid="hero-cta-login">
                <Link to="/login">Já tenho conta</Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs font-mono uppercase tracking-widest text-zinc-500">
              <span>▎ 12.451 VPS ativas</span>
              <span>▎ 99,993% uptime 90d</span>
              <span>▎ suporte 24/7 humano</span>
            </div>
          </div>
          <div className="md:col-span-4 border border-white/10 bg-[#121214]/60 backdrop-blur-sm p-6" data-testid="hero-terminal">
            <div className="font-mono text-xs text-zinc-500 flex items-center justify-between border-b border-white/10 pb-2">
              <span>root@flixvps ~</span>
              <span className="text-emerald-400">● online</span>
            </div>
            <pre className="font-mono text-xs leading-relaxed text-zinc-300 mt-3 whitespace-pre-wrap">
{`$ flixvps deploy --plan growth
> provisioning vps-a2f9...
> allocating 2 vCPU / 4GB RAM
> mounting 80GB NVMe
> configuring anti-ddos shield
> booting Ubuntu 22.04 LTS

READY in 47.312s
ipv4  203.0.113.42
ssh   ssh root@203.0.113.42

$ _`}
            </pre>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-white/10" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">// features</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                Infraestrutura séria, <br /> sem marketing barato.
              </h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-zinc-400 text-lg self-end">
              Cada VPS entrega o que promete. Métricas transparentes, sem asteriscos.
              O painel foi feito por sysadmins, para sysadmins.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-${i}`} className="bg-[#09090B] p-8 hover:bg-[#121214] transition-colors">
                <f.icon className="h-6 w-6 text-white mb-6" strokeWidth={1.5} />
                <div className="font-heading text-xl font-bold mb-2">{f.title}</div>
                <div className="text-sm text-zinc-500 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="planos" className="border-b border-white/10" data-testid="plans-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">// planos</div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                Escolha seu servidor.
              </h2>
              <p className="text-zinc-400 mt-3 max-w-lg">Todos os planos incluem anti-DDoS, painel próprio e migração grátis.</p>
            </div>
            <div className="text-xs font-mono text-zinc-500">Preços em BRL · sem taxa de setup</div>
          </div>

          {/* Location tabs (Paris / Brasil) */}
          <div className="inline-flex border border-white/10 rounded-sm p-1 bg-[#0e0e10] mb-10" data-testid="plans-location-tabs">
            {[
              { code: "PAR", label: "Paris", flag: "🇫🇷" },
              { code: "BR",  label: "Brasil", flag: "🇧🇷" },
            ].map((loc) => (
              <button
                key={loc.code}
                onClick={() => setLocationTab(loc.code)}
                data-testid={`location-tab-${loc.code}`}
                className={`px-6 py-2 text-sm font-mono uppercase tracking-widest transition-colors rounded-sm ${
                  locationTab === loc.code
                    ? "bg-white text-black"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <span className="mr-2">{loc.flag}</span>{loc.label}
              </button>
            ))}
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 ${
            locationTab === "BR" ? "lg:grid-cols-5" : "lg:grid-cols-3"
          } gap-4`}>
            {plans
              .filter((p) => (p.location_code || "PAR") === locationTab)
              .sort((a, b) => (a.plan_tier || 0) - (b.plan_tier || 0) || (a.price_brl - b.price_brl))
              .map((p) => (
              <div
                key={p.plan_id}
                data-testid={`plan-card-${p.slug}`}
                className={`relative border ${
                  p.is_featured
                    ? "border-pink-500/60 shadow-[0_0_40px_rgba(236,72,153,0.15)]"
                    : "border-white/10"
                } bg-[#121214] p-6 flex flex-col gap-5 rounded-sm hover:border-white/40 transition-colors`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm whitespace-nowrap">
                    ★ {p.badge}
                  </div>
                )}
                <div className="flex flex-col items-center text-center">
                  <div className="font-heading text-xl font-bold">{p.name.split(" ")[0]} {p.name.split(" ")[1]}</div>
                  {p.name.split(" ").slice(2).join(" ") && (
                    <div className="text-xs font-mono uppercase tracking-widest text-pink-400 mt-1">
                      {p.name.split(" ").slice(2).join(" ")}
                    </div>
                  )}
                </div>

                {locationTab !== "BR" && (
                  <p className="text-sm text-zinc-500 min-h-[40px]">{p.description}</p>
                )}

                <ul className="space-y-2 text-sm font-mono border-y border-white/10 py-4">
                  <li className="flex items-center justify-between"><span className="text-zinc-500">CPU</span><span>{p.cpu_cores} vCPU</span></li>
                  <li className="flex items-center justify-between"><span className="text-zinc-500">RAM</span><span>{p.ram_gb} GB</span></li>
                  <li className="flex items-center justify-between"><span className="text-zinc-500">SSD</span><span>{p.ssd_gb} GB NVMe</span></li>
                  <li className="flex items-center justify-between"><span className="text-zinc-500">REDE</span><span>{p.network_gbps || 1} Gbps</span></li>
                  {locationTab !== "BR" && (
                    <>
                      <li className="flex items-center justify-between"><span className="text-zinc-500">TRÁFEGO</span><span>{p.bandwidth_tb ? `${p.bandwidth_tb} TB` : "Ilimitado"}</span></li>
                      <li className="flex items-center justify-between"><span className="text-zinc-500">ANTI-DDOS</span><span>Incluso</span></li>
                      <li className="flex items-center justify-between"><span className="text-zinc-500">UPTIME SLA</span><span>99,9%</span></li>
                    </>
                  )}
                  <li className="flex items-center justify-between"><span className="text-zinc-500">LOCAL</span><span>{p.location}</span></li>
                </ul>

                <div className="flex flex-col items-center gap-1">
                  {p.original_price_brl && (
                    <div className="text-xs font-mono text-zinc-500">
                      DE <span className="line-through">{fmtBRL(p.original_price_brl)}</span>
                    </div>
                  )}
                  <div className="text-xs font-mono text-zinc-500">POR</div>
                  <div className="flex items-baseline gap-1">
                    <span className={`font-mono font-bold tracking-tight ${p.is_featured ? "text-pink-400 text-4xl" : "text-3xl"}`}>{fmtBRL(p.price_brl)}</span>
                    <span className="text-xs text-zinc-500 font-mono">/mês</span>
                  </div>
                </div>

                <Button asChild data-testid={`plan-cta-${p.slug}`} className={`rounded-sm ${
                  p.is_featured
                    ? "bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white hover:opacity-90"
                    : "bg-transparent text-white border border-white/20 hover:bg-white hover:text-black"
                } btn-sharp`}>
                  <Link to={`/checkout/${p.plan_id}`}>Contratar agora <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <button
                  onClick={() => { addItem(p); toast.success(`${p.name} adicionado ao carrinho`); }}
                  className="w-full flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 hover:border-white/40 py-2 transition-colors"
                  data-testid={`plan-cart-${p.slug}`}
                >
                  <ShoppingCart className="h-3.5 w-3.5" /> Adicionar ao carrinho
                </button>

                {p.is_featured && locationTab === "BR" && (
                  <div className="text-center text-[10px] font-mono uppercase tracking-widest text-pink-400 mt-1">
                    ↑ melhor custo-benefício ↑
                  </div>
                )}
              </div>
            ))}
          </div>

          {locationTab === "BR" && (
            <div className="mt-6 text-center text-xs font-mono text-zinc-500">
              ⚡ Planos Brasil: provisionamento automático após confirmação do PIX · credenciais após validação do acesso
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">// faq</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-10">
            Perguntas objetivas.
          </h2>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-6" data-testid={`faq-${i}`}>
                <summary className="flex justify-between items-center cursor-pointer list-none">
                  <span className="font-heading text-lg">{f.q}</span>
                  <span className="text-zinc-500 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-white/10 bg-[#0c0c0e]">
        <div className="max-w-7xl mx-auto px-6 py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Pronto para migrar?</h3>
            <p className="text-zinc-400 mt-2">Migração assistida grátis. Sem contrato de fidelidade.</p>
          </div>
          <Button asChild size="lg" className="rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp px-8" data-testid="footer-cta">
            <Link to="/register">Criar conta agora <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 text-sm text-zinc-500">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 flex items-center justify-center border border-white/20">
                <Terminal className="h-4 w-4" strokeWidth={2} />
              </div>
              <div className="font-heading font-black text-white">Flix VPS</div>
            </div>
            <p>Infraestrutura sólida, sem enrolação. Feito por quem opera servidores há mais de uma década.</p>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">Produto</div>
            <ul className="space-y-2">
              <li><a href="#planos" className="hover:text-white">Planos VPS</a></li>
              <li><a href="#features" className="hover:text-white">Features</a></li>
              <li><Link to="/login" className="hover:text-white">Área do cliente</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">Empresa</div>
            <ul className="space-y-2">
              <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              <li>SLA 99.99%</li>
              <li>CNPJ 00.000.000/0001-00</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">Status</div>
            <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 dot-blink" /> Todos os sistemas online</div>
            <div className="mt-3 font-mono text-xs">v1.0 · Global network</div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-white/10 flex justify-between text-xs font-mono">
          <span>© 2026 Flix VPS. Todos os direitos reservados.</span>
          <span>flixvps.com.br</span>
        </div>
      </footer>
    </div>
  );
}

